import { Button, Card, CardContent, cn, Input, Label } from '@crm/ui';
import { ChevronDown, Plus } from 'lucide-react';
import { useState } from 'react';
import type { CategoryTemplate } from '../api';
import { ServiceTemplateRow, type ServicoEdicao } from './service-template-row';

interface CategoryTemplateCardProps {
  categoria: CategoryTemplate;
  aberta: boolean;
  onToggle: () => void;
  onCreateService: (input: { name: string; durationMinutes: number; price: number }) => void;
  /** Servicos com edicao pendente (ainda nao salva) desta categoria, por id. */
  pendentes: Record<string, ServicoEdicao>;
  onChangeService: (id: string, input: ServicoEdicao) => void;
  onRemoveService: (id: string) => void;
}

/**
 * Uma categoria em accordion: fechada mostra so o nome e a contagem, aberta
 * traz a lista de servicos. O nome e fixo (as 5 categorias nao mudam - ver
 * `templates-assistant.tsx`); so abre e fecha, quem decide avancar e o
 * assistente do salao, nao esta tela.
 */
export function CategoryTemplateCard({
  categoria,
  aberta,
  onToggle,
  onCreateService,
  pendentes,
  onChangeService,
  onRemoveService,
}: CategoryTemplateCardProps) {
  const [novoServico, setNovoServico] = useState<{
    name: string;
    durationMinutes: string;
    price: string;
  } | null>(null);

  return (
    <Card variant="flush">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        aria-expanded={aberta}
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            !aberta && '-rotate-90',
          )}
        />
        <span className="font-medium">{categoria.name}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
          {categoria.services.length}
        </span>
      </button>

      {aberta ? (
        <CardContent className="space-y-4 border-t border-border pt-4">
          <div className="space-y-2">
            {categoria.services.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum servico ainda.</p>
            ) : (
              <div className="space-y-2">
                {categoria.services.map((servico) => (
                  <ServiceTemplateRow
                    key={servico.id}
                    servico={servico}
                    pendente={pendentes[servico.id]}
                    onChange={(input) => onChangeService(servico.id, input)}
                    onRemove={() => onRemoveService(servico.id)}
                  />
                ))}
              </div>
            )}

            {novoServico ? (
              <form
                className="flex flex-wrap items-end gap-2 rounded-card border border-dashed border-border p-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!novoServico.name.trim()) return;

                  onCreateService({
                    name: novoServico.name.trim(),
                    durationMinutes: Number(novoServico.durationMinutes) || 30,
                    price: Number(novoServico.price) || 0,
                  });
                  setNovoServico(null);
                }}
              >
                <div className="min-w-[160px] flex-1 space-y-1">
                  <Label htmlFor={`novo-servico-nome-${categoria.id}`}>Nome do servico</Label>
                  <Input
                    id={`novo-servico-nome-${categoria.id}`}
                    autoFocus
                    value={novoServico.name}
                    onChange={(event) =>
                      setNovoServico((atual) => atual && { ...atual, name: event.target.value })
                    }
                  />
                </div>
                <div className="w-24 space-y-1">
                  <Label htmlFor={`novo-servico-duracao-${categoria.id}`}>Minutos</Label>
                  <Input
                    id={`novo-servico-duracao-${categoria.id}`}
                    type="number"
                    min={5}
                    value={novoServico.durationMinutes}
                    onChange={(event) =>
                      setNovoServico(
                        (atual) => atual && { ...atual, durationMinutes: event.target.value },
                      )
                    }
                  />
                </div>
                <div className="w-28 space-y-1">
                  <Label htmlFor={`novo-servico-preco-${categoria.id}`}>Preco padrao</Label>
                  <Input
                    id={`novo-servico-preco-${categoria.id}`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={novoServico.price}
                    onChange={(event) =>
                      setNovoServico((atual) => atual && { ...atual, price: event.target.value })
                    }
                  />
                </div>
                <Button type="submit" size="sm">
                  Adicionar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setNovoServico(null)}
                >
                  Cancelar
                </Button>
              </form>
            ) : (
              <Button
                size="sm"
                onClick={() => setNovoServico({ name: '', durationMinutes: '30', price: '' })}
              >
                <Plus className="h-4 w-4" />
                Novo servico
              </Button>
            )}
          </div>
        </CardContent>
      ) : null}
    </Card>
  );
}
